// Angular Files
import {
 DOCUMENT, Inject, Injectable, NgZone
} from '@angular/core';

// Component Files
import { RumEvent } from './rum.models';
import { TellerOnlineRumService } from './rum.service';

/** No input for this long and the user is treated as away. */
const IDLE_MS = 30_000;
const MIN_REPORT_MS = 1000;

/** Activity is stamped at most this often; the timer does the accumulating. */
const ACTIVITY_THROTTLE_MS = 1000;
const TICK_MS = 5000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'pointermove', 'touchstart'];

/** Sums the time the tab was visible and the user was active, sent as `engaged_time`. */
@Injectable({ providedIn: 'root' })
export class TellerOnlineRumEngagementService {
    private _started = false;

    private _engagedMs = 0;

    private _lastTick = 0;

    private _lastActivity = 0;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ngZone: NgZone,
        private rumService: TellerOnlineRumService
    ) { }

    public start(): void {
        if (this._started || !this.rumService.enabled) return;

        this._started = true;
        this._lastTick = Date.now();
        this._lastActivity = this._lastTick;

        this.ngZone.runOutsideAngular(() => {
            for (const name of ACTIVITY_EVENTS) {
                this.document.addEventListener(name, this._onActivity, { passive: true });
            }
            this.document.addEventListener('visibilitychange', this._onVisibilityChange);
            this.document.defaultView?.addEventListener('pagehide', this._flush);
            setInterval(() => this._accumulate(Date.now()), TICK_MS);
        });
    }

    private _accumulate(now: number): void {
        if (this.document.visibilityState !== 'hidden') {
            // Only the stretch before the idle cut-off counts
            const until = Math.min(now, this._lastActivity + IDLE_MS);
            this._engagedMs += Math.max(0, until - this._lastTick);
        }
        this._lastTick = now;
    }

    private readonly _onActivity = (): void => {
        const now = Date.now();
        if (now - this._lastActivity >= ACTIVITY_THROTTLE_MS) this._lastActivity = now;
    };

    private readonly _onVisibilityChange = (): void => {
        if (this.document.visibilityState === 'hidden') {
            this._flush();
        } else {
            this._lastTick = Date.now();
            this._lastActivity = this._lastTick;
        }
    };

    private readonly _flush = (): void => {
        this._accumulate(Date.now());
        if (this._engagedMs >= MIN_REPORT_MS) {
            this.rumService.recordEvent(RumEvent.engagedTime, { ms: Math.round(this._engagedMs) });
            this._engagedMs = 0;
        }
    };
}
